import { isChief, type ActorDTO, type NodeDTO } from "@roomate/core";

/** Why no agent can pick up work right now. `null` = at least one agent can work. */
export type NoWorkerReason = "no-agents" | "no-machine" | null;

/**
 * An agent can work when it is active and either runs in a cloud sandbox (no machine of ours needed)
 * or is hosted by a member node that is online. Merge-only nodes run no agents.
 */
export function noWorkerReason(actors: readonly ActorDTO[], nodes: readonly NodeDTO[]): NoWorkerReason {
  const agents = actors.filter((a) => a.kind === "agent" && a.active && !a.archived && !isChief(a) && a.maxParallel > 0);
  if (agents.length === 0) return "no-agents";
  const hosted = new Set(nodes.filter((n) => n.online && n.role === "member").flatMap((n) => n.agentIds));
  return agents.some((a) => a.host !== "local" || hosted.has(a.id)) ? null : "no-machine";
}

/** Stands in for the hub address when the page cannot tell it (the page is on loopback). */
export const HUB_ADDRESS_PLACEHOLDER = "<hub-address>";

/**
 * The command that connects this machine to this hub. The token is piped in, never printed. A null
 * hub address (loopback origin: a node proxy or the Vite dev server) gets the placeholder, not a wrong URL.
 */
export function nodeInitCommand(hubUrl: string | null): string {
  return `roomate node init --hub ${hubUrl ?? HUB_ADDRESS_PLACEHOLDER} --token-stdin`;
}
